import { AdvancedMarker } from "@vis.gl/react-google-maps";
import { memo, useCallback, useEffect, useRef } from "react";
import { getSportIcon } from "@/lib/sport-icons";
import { cn } from "@/lib/utils";

interface MapMarkerProps {
  locationId: string;
  position: google.maps.LatLngLiteral;
  hasSportField: boolean | null;
  primaryType: string | null;
  sportTypes?: string[] | null;
  name: string;
  ownership?: string;
  isReviewed?: boolean;
  isRejected?: boolean;
  approvedByCity?: boolean;
  approvedBySponsor?: boolean;
  isMuted?: boolean;
  isDimmed?: boolean;
  source?: string | null;
  isProcessing?: boolean;
  isSelected?: boolean;
  isInCollection?: boolean;
  collectionColor?: string;
  isDraggable?: boolean;
  onDragEnd?: (lat: number, lng: number) => void;
  onClick?: (locationId: string) => void;
  onMouseEnter?: (locationId: string) => void;
  onMouseLeave?: (locationId: string) => void;
  setMarkerRef?: (marker: google.maps.marker.AdvancedMarkerElement | null, key: string) => void;
}

function getMarkerColor(isReviewed?: boolean, isRejected?: boolean, source?: string | null): string {
  if (isRejected) return "#9E9E9E"; // gray
  if (source === "city" || source === "external_import") return "#f97316"; // orange for imported locations
  if (isReviewed) return "#22c55e"; // green
  return "#6366f1"; // indigo (unreviewed default)
}

export const MapMarker = memo(function MapMarker({
  locationId,
  position,
  hasSportField,
  primaryType,
  sportTypes,
  name,
  ownership,
  isReviewed,
  isRejected,
  approvedByCity,
  approvedBySponsor,
  isMuted,
  isDimmed,
  source,
  isProcessing,
  isSelected,
  isInCollection,
  collectionColor,
  isDraggable,
  onDragEnd,
  onClick,
  onMouseEnter,
  onMouseLeave,
  setMarkerRef,
}: MapMarkerProps) {
  const SportIcon = getSportIcon(sportTypes, primaryType);
  const color = isMuted ? "#9E9E9E" : getMarkerColor(isReviewed, isRejected, source ?? undefined);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);

  const handleClick = useCallback(() => {
    onClick?.(locationId);
  }, [onClick, locationId]);

  const handleMouseEnter = useCallback(() => {
    onMouseEnter?.(locationId);
  }, [onMouseEnter, locationId]);

  const handleMouseLeave = useCallback(() => {
    onMouseLeave?.(locationId);
  }, [onMouseLeave, locationId]);

  const ref = useCallback(
    (marker: google.maps.marker.AdvancedMarkerElement | null) => {
      markerRef.current = marker;
      setMarkerRef?.(marker, locationId);
    },
    [setMarkerRef, locationId]
  );

  // Listen to dragend event on the underlying marker element
  useEffect(() => {
    const marker = markerRef.current;
    if (!marker || !isDraggable || !onDragEnd) return;

    const listener = marker.addListener("dragend", () => {
      const pos = marker.position;
      if (pos) {
        const lat = typeof pos.lat === "function" ? (pos as google.maps.LatLng).lat() : (pos as google.maps.LatLngLiteral).lat;
        const lng = typeof pos.lng === "function" ? (pos as google.maps.LatLng).lng() : (pos as google.maps.LatLngLiteral).lng;
        onDragEnd(lat, lng);
      }
    });

    return () => {
      listener.remove();
    };
  }, [isDraggable, onDragEnd]);

  const zIndex = isDraggable ? 1200 : isSelected ? 1000 : isMuted ? 1 : isReviewed ? 901 : 902;

  return (
    <AdvancedMarker
      ref={ref}
      position={position}
      title={name}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      zIndex={zIndex}
      draggable={isDraggable}
    >
      <div
        className={cn(
          "relative flex flex-col items-center cursor-pointer",
          "transition-transform duration-150 ease-out",
          isSelected ? "scale-[1.25]" : "hover:scale-[1.15]",
          "origin-bottom"
        )}
        style={
          isDimmed && !isSelected
            ? { opacity: 0.3 }
            : (isMuted || isRejected) && !isSelected
              ? { opacity: 0.45 }
              : undefined
        }
      >
        {/* Selected glow ring */}
        {isSelected && !isProcessing && (
          <div className="absolute -inset-2 rounded-full bg-primary/20 animate-pulse -z-10" />
        )}
        {isProcessing ? (
          <div className="animate-bounce" style={{ animationDuration: "1.5s" }}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="12" cy="12" r="8" fill="#3b82f6" fillOpacity="0.3" />
              <circle cx="12" cy="12" r="5" fill="#3b82f6" />
            </svg>
          </div>
        ) : (
          <svg
            width="34"
            height="48"
            viewBox="0 0 34 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17 0C7.611 0 0 7.611 0 17c0 12.75 17 31 17 31s17-18.25 17-31C34 7.611 26.389 0 17 0z"
              fill={color}
              stroke="white"
              strokeWidth="1.5"
            />
            <foreignObject x="5" y="5" width="24" height="24">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  height: "100%",
                }}
              >
                <SportIcon size={18} stroke={1.5} color="white" />
              </div>
            </foreignObject>
            {/* Approved by city badge */}
            {approvedByCity && (
              <>
                <circle cx="27" cy="38" r="6" fill="#1d4ed8" stroke="white" strokeWidth="1" />
                <path
                  d="M24 38l1.5 1.5 3-3"
                  stroke="white"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </>
            )}
            {/* Approved by sponsor badge */}
            {approvedBySponsor && !approvedByCity && (
              <>
                <circle cx="27" cy="38" r="6" fill="#059669" stroke="white" strokeWidth="1" />
                <path
                  d="M24 38l1.5 1.5 3-3"
                  stroke="white"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </>
            )}
          </svg>
        )}
        {isInCollection && (
          <div
            className="absolute -top-1 -right-1 size-4 rounded-full border-2 border-white flex items-center justify-center animate-badge-pop"
            style={{ backgroundColor: collectionColor ?? "#3b82f6" }}
          >
            <svg width="8" height="8" viewBox="0 0 8 8">
              <path
                d="M1 4 l2 2 l3-4"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </div>
        )}
      </div>
    </AdvancedMarker>
  );
});
